/**
 * Iconos de trazo del prototipo aprobado (docs/design/ContaQuick-Prototipo.html).
 *
 * Van como componentes y no como una fuente de iconos ni un paquete de npm por tres razones:
 * son doce, el prototipo los dibuja a 24×24 con trazo de 2, y `stroke="currentColor"` deja que
 * cada uno tome el color de su contexto — el mismo icono va gris en la navegación, blanco sobre
 * el activo y acentuado dentro de un aviso, sin una sola línea de CSS extra.
 *
 * Hasta ahora la SPA no tenía ni un SVG: la campana era el emoji 🔔, que cada sistema operativo
 * dibuja distinto y que un lector de pantalla lee en voz alta.
 */

interface PropiedadesDeIcono {
  /** Lado en píxeles. 15 en la navegación y las notificaciones, 14 en los botones, 13 en el buscador. */
  tamano?: number
}

function Trazo({ tamano = 15, children }: PropiedadesDeIcono & { children: React.ReactNode }) {
  return (
    <svg
      width={tamano}
      height={tamano}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

export function IconoTablero(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </Trazo>
  )
}

export function IconoClientes(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Trazo>
  )
}

export function IconoCatalogo(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
      <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
      <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    </Trazo>
  )
}

export function IconoPolizas(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <path d="M12 7v14" />
      <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
    </Trazo>
  )
}

export function IconoCfdi(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </Trazo>
  )
}

export function IconoContabilizacion(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </Trazo>
  )
}

export function IconoReportes(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="M18 17V9" />
      <path d="M13 17V5" />
      <path d="M8 17v-3" />
    </Trazo>
  )
}

export function IconoContabilidadElectronica(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <path d="M14.5 21.7a.5.5 0 0 0 .94-.03l6.5-19a.5.5 0 0 0-.63-.63l-19 6.5a.5.5 0 0 0-.03.94l7.93 3.18a2 2 0 0 1 1.11 1.11z" />
      <path d="M21.85 2.15 10.91 13.09" />
    </Trazo>
  )
}

export function IconoBitacora(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3 6h.01" />
      <path d="M3 12h.01" />
      <path d="M3 18h.01" />
    </Trazo>
  )
}

export function IconoColaboradores(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <path d="M18 21a8 8 0 0 0-16 0" />
      <circle cx="10" cy="8" r="5" />
      <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
    </Trazo>
  )
}

export function IconoCampana(p: PropiedadesDeIcono) {
  return (
    <Trazo {...p}>
      <path d="M10.268 21a2 2 0 0 0 3.464 0" />
      <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
    </Trazo>
  )
}

/** Aviso: 16 px en los avisos críticos, 15 px en las notificaciones. */
export function IconoAlerta(p: PropiedadesDeIcono) {
  return (
    <Trazo tamano={16} {...p}>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </Trazo>
  )
}

/** Lupa del buscador: 13 px, posicionada sobre el campo. */
export function IconoBuscar(p: PropiedadesDeIcono) {
  return (
    <Trazo tamano={13} {...p}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </Trazo>
  )
}

/** Chevron de fila navegable: 14 px, última celda de la tabla de clientes. */
export function IconoChevron(p: PropiedadesDeIcono) {
  return (
    <Trazo tamano={14} {...p}>
      <path d="m9 18 6-6-6-6" />
    </Trazo>
  )
}

/**
 * L3 · El logotipo de Microsoft del botón de acceso. Es la única excepción a todo lo de
 * arriba y tiene que serlo: no es un icono de trazo que tome `currentColor`, son cuatro
 * cuadros rellenos con los colores de marca de Microsoft, que su guía de identidad para
 * botones de inicio de sesión no deja recolorear. Por eso no usa `Trazo`.
 *
 * Va aquí, y no como archivo aparte, para que el siguiente que busque un SVG en la SPA
 * encuentre todos en el mismo lugar.
 */
export function LogoDeMicrosoft({ tamano = 16 }: PropiedadesDeIcono) {
  return (
    <svg width={tamano} height={tamano} viewBox="0 0 23 23" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
      <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </svg>
  )
}
